import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, Loader2, Sparkles, Eye, EyeOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMediaUrl } from "@/lib/api";
import { DecoFall, DecoItem } from "@/components/price/DecorationsLayer";

const LIBRARY = [
  ["Toile", "/deco/hw_web_corner.png"], ["Araignée", "/deco/hw_spider.png"], ["Chauve-souris", "/deco/hw_bat.png"],
  ["Citrouille", "/deco/hw_pumpkin.png"], ["Fantôme", "/deco/hw_ghost.png"], ["Guirlande", "/deco/xm_garland.png"],
  ["Bonnet", "/deco/xm_hat.png"], ["Sapin", "/deco/xm_tree.png"], ["Cadeau", "/deco/xm_gift.png"], ["Flocon", "/deco/xm_snowflake.png"],
];
const ANIMS = [
  ["none", "Aucune"], ["float", "Flotter"], ["swing", "Balancer"], ["spin", "Tourner"], ["pulse", "Pulser"], ["fall", "Tomber (pluie)"],
];
const FIELDS = ["name", "image_url", "screens", "x_pct", "y_pct", "width_pct", "rotation", "opacity", "flip", "animation", "layer", "start_at", "end_at", "active", "pack"];
const pick = (d) => Object.fromEntries(FIELDS.map((k) => [k, d[k]]));
const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function StudioLiveTab() {
  const [items, setItems] = useState([]);
  const [screen, setScreen] = useState("client");
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const saveTimers = useRef({});

  const load = useCallback(async () => {
    const r = await api.get("/admin/price/decorations");
    setItems(r.data?.items || []);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Chargement impossible")); }, [load]);

  const save = useCallback((d) => {
    clearTimeout(saveTimers.current[d.id]);
    saveTimers.current[d.id] = setTimeout(() => {
      api.put(`/admin/price/decorations/${d.id}`, pick(d)).catch((e) => toast.error(e?.response?.data?.detail?.[0]?.msg || "Sauvegarde échouée"));
    }, 500);
  }, []);

  const update = (id, patch) => {
    setItems((cur) => cur.map((d) => {
      if (d.id !== id) return d;
      const next = { ...d, ...patch };
      save(next);
      return next;
    }));
  };

  const create = async (name, url) => {
    try {
      const r = await api.post("/admin/price/decorations", { name, image_url: url, screens: [screen], x_pct: 50, y_pct: 40, width_pct: 25 });
      setItems((c) => [...c, r.data.item]);
      setSelId(r.data.item.id);
      toast.success("Déco ajoutée — glisse-la sur l'aperçu");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await create(file.name.replace(/\.[^.]+$/, "").slice(0, 60), r.data.url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload échoué"); } finally { setBusy(false); }
  };

  const pack = async (name, on, label = name) => {
    if (!on && !window.confirm(`Retirer toutes les décos du pack ${label} ?`)) return;
    setBusy(true);
    try {
      if (on) await api.post(`/admin/price/decorations/packs/${name}`);
      else await api.delete(`/admin/price/decorations/packs/${name}`);
      await load();
      toast.success(on ? `Pack ${label} activé en direct` : `Pack ${label} retiré`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette déco ?")) return;
    await api.delete(`/admin/price/decorations/${id}`);
    setItems((c) => c.filter((d) => d.id !== id));
    setSelId(null);
  };

  const visible = items.filter((d) => d.screens?.includes(screen));
  const sel = items.find((d) => d.id === selId);

  return (
    <div className="space-y-4" data-testid="studio-live-tab">
      <StudioToolbar screen={screen} setScreen={setScreen} busy={busy} onUpload={upload} onPack={pack} />
      <div className="grid gap-5">
        <PhonePreview screen={screen} items={visible} selId={selId} onSelect={setSelId} onMove={update} />
        <div className="space-y-4">
          <Library onAdd={create} />
          <DecoList items={items} selId={selId} onSelect={setSelId} onToggle={(d) => update(d.id, { active: !d.active })} />
          {sel && <DecoEditor d={sel} onChange={(p) => update(sel.id, p)} onDelete={() => remove(sel.id)} />}
        </div>
      </div>
    </div>
  );
}

function StudioToolbar({ screen, setScreen, busy, onUpload, onPack }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white p-3 street-border-thin">
      <div className="flex rounded-xl street-border-thin overflow-hidden">
        {[["client", "Accueil passager"], ["driver", "Accueil conducteur"]].map(([k, l]) => (
          <button key={k} onClick={() => setScreen(k)} data-testid={`studio-screen-${k}`}
            className={"px-3 py-2 text-[12px] font-black " + (screen === k ? "bg-primary text-primary-foreground" : "bg-white")}>{l}</button>
        ))}
      </div>
      <label className="street-press inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-accent px-3 py-2 text-[12px] font-black street-border-thin" data-testid="studio-upload">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ajouter mon image (PNG)
        <input type="file" accept="image/png,image/webp,image/gif,image/jpeg" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="studio-upload-input" />
      </label>
      {[["halloween", "Halloween"], ["noel", "Noël"]].map(([k, l]) => (
        <div key={k} className="inline-flex overflow-hidden rounded-xl street-border-thin">
          <button onClick={() => onPack(k, true, l)} disabled={busy} data-testid={`studio-pack-${k}-on`} className="inline-flex items-center gap-1 bg-white px-3 py-2 text-[12px] font-black">
            <Sparkles className="h-3.5 w-3.5" /> Pack {l}
          </button>
          <button onClick={() => onPack(k, false, l)} disabled={busy} data-testid={`studio-pack-${k}-off`} className="border-l-2 border-black bg-white px-2 py-2" title="Retirer le pack">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

function PhonePreview({ screen, items, selId, onSelect, onMove }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const onDown = (d) => (e) => {
    e.preventDefault();
    onSelect(d.id);
    drag.current = { id: d.id };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMoveEv = (e) => {
    if (!drag.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - r.left) / r.width) * 1000) / 10;
    const y = Math.round(((e.clientY - r.top) / r.height) * 1000) / 10;
    onMove(drag.current.id, { x_pct: Math.max(-10, Math.min(110, x)), y_pct: Math.max(-10, Math.min(110, y)) });
  };
  return (
    <div className="mx-auto self-start rounded-[44px] bg-black p-3 shadow-xl" style={{ width: 320 }}>
      <div
        ref={ref}
        className="relative overflow-hidden rounded-[34px]"
        style={{ height: 640, background: "hsl(15 100% 96%)" }}
        onPointerMove={onMoveEv}
        onPointerUp={() => { drag.current = null; }}
        onPointerLeave={() => { drag.current = null; }}
        onClick={(e) => { if (e.target === e.currentTarget) onSelect(null); }}
        data-testid="studio-phone"
      >
        <MockHome screen={screen} />
        {items.filter((d) => d.active).map((d) =>
          d.animation === "fall" ? (
            <div key={d.id} className="pointer-events-none absolute inset-0"><DecoFall d={d} /></div>
          ) : (
            <DecoItem key={d.id} d={d} selected={d.id === selId} onPointerDown={onDown(d)} />
          )
        )}
      </div>
      <p className="mt-2 text-center text-[11px] font-bold text-white/70">Glisse les images pour les placer · mis à jour en direct</p>
    </div>
  );
}

function MockHome({ screen }) {
  const block = "rounded-2xl bg-white street-border-thin";
  return (
    <div className="pointer-events-none absolute inset-0 p-4 pt-8 opacity-90">
      <div className="flex items-center justify-between">
        <div className="h-8 w-24 rounded-xl bg-primary/80" />
        <div className="flex gap-1.5">{[0, 1, 2].map((i) => <div key={i} className="h-8 w-8 rounded-full bg-white street-border-thin" />)}</div>
      </div>
      <div className="mt-5 h-3 w-14 rounded bg-foreground/20" />
      <div className="mt-2 h-7 w-32 rounded bg-foreground/70" />
      {screen === "client" ? (
        <>
          <div className="mt-5 h-28 rounded-3xl bg-primary street-border-thin" />
          <div className={`mt-4 h-64 ${block}`} />
        </>
      ) : (
        <>
          <div className={`mt-5 h-24 ${block}`} />
          <div className="mt-4 h-20 rounded-3xl bg-primary street-border-thin" />
          <div className={`mt-3 h-14 ${block}`} />
          <div className={`mt-4 h-40 ${block}`} />
        </>
      )}
    </div>
  );
}

function Library({ onAdd }) {
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin">
      <div className="mb-2 text-[11px] font-black uppercase tracking-widest text-foreground/50">Bibliothèque (clic = ajouter)</div>
      <div className="flex flex-wrap gap-2">
        {LIBRARY.map(([n, u]) => (
          <button key={u} onClick={() => onAdd(n, u)} title={n} data-testid={`studio-lib-${n}`}
            className="street-press grid h-14 w-14 place-items-center rounded-xl bg-secondary/40 p-1 street-border-thin">
            <img src={resolveMediaUrl(u)} alt={n} className="h-full w-full object-contain" />
          </button>
        ))}
      </div>
    </div>
  );
}

function DecoList({ items, selId, onSelect, onToggle }) {
  if (!items.length) return <div className="rounded-2xl bg-white p-4 text-[13px] text-foreground/60 street-border-thin">Aucune déco. Active un pack ou ajoute une image.</div>;
  return (
    <div className="max-h-64 overflow-y-auto rounded-2xl bg-white p-2 street-border-thin" data-testid="studio-list">
      {items.map((d) => (
        <div key={d.id} onClick={() => onSelect(d.id)} data-testid={`studio-row-${d.id}`}
          className={"flex cursor-pointer items-center gap-2 rounded-xl p-2 " + (d.id === selId ? "bg-primary/10" : "hover:bg-secondary/40")}>
          <img src={resolveMediaUrl(d.image_url)} alt="" className="h-8 w-8 object-contain" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-black">{d.name}</div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">
              {(d.screens || []).map((s) => (s === "client" ? "Passager" : "Conducteur")).join(" + ")}{d.pack ? ` · pack ${d.pack}` : ""}
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onToggle(d); }} data-testid={`studio-toggle-${d.id}`}
            className={"rounded-lg p-1.5 street-border-thin " + (d.active ? "bg-accent" : "bg-white")} title={d.active ? "Visible" : "Masquée"}>
            {d.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        </div>
      ))}
    </div>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, testid }) {
  return (
    <label className="block">
      <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-foreground/50"><span>{label}</span><span>{value}</span></div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#FF3B3B]" data-testid={testid} />
    </label>
  );
}

function DecoEditor({ d, onChange, onDelete }) {
  const toggleScreen = (s) => {
    const next = d.screens.includes(s) ? d.screens.filter((x) => x !== s) : [...d.screens, s];
    if (next.length) onChange({ screens: next });
  };
  return (
    <div className="space-y-3 rounded-2xl bg-white p-4 street-border-thin" data-testid="studio-editor">
      <input value={d.name} onChange={(e) => onChange({ name: e.target.value })} className="w-full rounded-xl bg-secondary/40 px-3 py-2 text-[14px] font-black street-border-thin" data-testid="studio-name" />
      <div className="flex gap-2">
        {[["client", "Passager"], ["driver", "Conducteur"]].map(([k, l]) => (
          <button key={k} onClick={() => toggleScreen(k)} data-testid={`studio-ed-screen-${k}`}
            className={"rounded-xl px-3 py-1.5 text-[12px] font-black street-border-thin " + (d.screens.includes(k) ? "bg-primary text-primary-foreground" : "bg-white")}>{l}</button>
        ))}
        <button onClick={() => onChange({ flip: !d.flip })} data-testid="studio-flip" className={"rounded-xl px-3 py-1.5 text-[12px] font-black street-border-thin " + (d.flip ? "bg-accent" : "bg-white")}>Miroir</button>
      </div>
      <Slider label="Taille (%)" value={d.width_pct} min={2} max={120} onChange={(v) => onChange({ width_pct: v })} testid="studio-size" />
      <Slider label="Rotation (°)" value={d.rotation} min={-180} max={180} onChange={(v) => onChange({ rotation: v })} testid="studio-rotation" />
      <Slider label="Opacité" value={d.opacity} min={0.05} max={1} step={0.05} onChange={(v) => onChange({ opacity: v })} testid="studio-opacity" />
      <div className="grid grid-cols-2 gap-2">
        <Slider label="Position X (%)" value={d.x_pct} min={-10} max={110} onChange={(v) => onChange({ x_pct: v })} testid="studio-x" />
        <Slider label="Position Y (%)" value={d.y_pct} min={-10} max={110} onChange={(v) => onChange({ y_pct: v })} testid="studio-y" />
      </div>
      <label className="block text-[11px] font-black uppercase tracking-widest text-foreground/50">Animation
        <select value={d.animation} onChange={(e) => onChange({ animation: e.target.value })} className="mt-1 w-full rounded-xl bg-white px-2 py-2 text-[13px] font-bold normal-case tracking-normal text-foreground street-border-thin" data-testid="studio-animation">
          {ANIMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        {[["start_at", "Début (optionnel)"], ["end_at", "Fin (optionnel)"]].map(([k, l]) => (
          <label key={k} className="block text-[11px] font-black uppercase tracking-widest text-foreground/50">{l}
            <input type="datetime-local" value={toLocal(d[k])} onChange={(e) => onChange({ [k]: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="mt-1 w-full rounded-xl bg-white px-2 py-2 text-[12px] font-bold normal-case tracking-normal text-foreground street-border-thin" data-testid={`studio-${k}`} />
          </label>
        ))}
      </div>
      <div className="flex justify-between">
        <button onClick={() => onChange({ active: !d.active })} data-testid="studio-active" className={"inline-flex items-center gap-1 rounded-xl px-3 py-2 text-[12px] font-black street-border-thin " + (d.active ? "bg-accent" : "bg-white")}>
          {d.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}{d.active ? "Visible" : "Masquée"}
        </button>
        <button onClick={onDelete} data-testid="studio-delete" className="inline-flex items-center gap-1 rounded-xl bg-white px-3 py-2 text-[12px] font-black text-red-600 street-border-thin">
          <Trash2 className="h-4 w-4" /> Supprimer
        </button>
      </div>
      <p className="text-[11px] text-foreground/50"><Plus className="inline h-3 w-3" /> Sauvegarde automatique — les utilisateurs voient le changement en moins d&apos;une minute.</p>
    </div>
  );
}
